/*!

=========================================================
* Vision UI Free React - v1.0.0
=========================================================

* Product Page: https://www.creative-tim.com/product/vision-ui-free-react
* Copyright 2021 Creative Tim (https://www.creative-tim.com/)
* Licensed under MIT (https://github.com/creativetimofficial/vision-ui-free-react/blob/master LICENSE.md)

* Design and Coded by Simmmple & Creative Tim

=========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.

*/

// @mui material components
import Button from "@mui/material/Button";
import { styled } from "@mui/material/styles";

// Per-colour gradient stops for the 3D raised effect.
// Each entry: from (highlight top-left), mid (base colour), to (shadow bottom-right)
const BUTTON_GRADIENTS = {
  info:      { from: "#5ab5ff", mid: "#0075ff", to: "#0050b3" },
  success:   { from: "#4ae8a4", mid: "#01b574", to: "#007a4e" },
  error:     { from: "#ff7761", mid: "#e31a1a", to: "#9e0000" },
  warning:   { from: "#ffd35c", mid: "#ffb547", to: "#c47b00" },
  primary:   { from: "#c4a8ff", mid: "#4318ff", to: "#2a00b8" },
  secondary: { from: "#d8e8f8", mid: "#8fa8c8", to: "#627594" },
  white:     { from: "#ffffff", mid: "#f2f6ff", to: "#dce6ff" },
  dark:      { from: "#4a6080", mid: "#0a1628", to: "#040c1a" },
  light:     { from: "#ffffff", mid: "#e9ecef", to: "#c4cdd6" },
  text:      { from: "#8aaacf", mid: "#4a6080", to: "#2d3e55" },
};

// Colours that need dark text
const DARK_TEXT_COLOURS = new Set(["white", "light", "secondary"]);

export default styled(Button)(({ theme, ownerState }) => {
  const { palette, functions, borders } = theme;
  const { color, variant, size, circular, iconOnly } = ownerState;

  const { white, dark } = palette;
  const { boxShadow, pxToRem, rgba } = functions;
  const { borderRadius } = borders;

  // ------------------------------------------------------------------
  // Contained – rich 3-stop gradient + layered 3D shadows
  // ------------------------------------------------------------------
  const containedStyles = () => {
    const g = BUTTON_GRADIENTS[color] || BUTTON_GRADIENTS.info;
    const bg = `linear-gradient(145deg, ${g.from} 0%, ${g.mid} 50%, ${g.to} 100%)`;
    const textColor = DARK_TEXT_COLOURS.has(color) ? dark.main : white.main;
    const glowColor = palette[color] ? rgba(palette[color].main, 0.45) : rgba(dark.main, 0.35);

    const shadow     = `inset 0 1px 0 rgba(255,255,255,0.28), 0 4px 12px rgba(0,0,0,0.22), 0 2px 6px ${glowColor}`;
    const hoverShad  = `inset 0 1px 0 rgba(255,255,255,0.32), 0 8px 20px rgba(0,0,0,0.28), 0 4px 10px ${glowColor}`;
    const activeShad = `inset 0 2px 6px rgba(0,0,0,0.35), 0 1px 3px rgba(0,0,0,0.2)`;

    return {
      background: bg,
      color: textColor,
      boxShadow: shadow,

      "&:hover": {
        background: bg,
        filter: "brightness(1.1)",
        boxShadow: hoverShad,
        transform: "translateY(-2px)",
      },

      "&:active, &:active:hover": {
        filter: "brightness(0.94)",
        boxShadow: activeShad,
        transform: "translateY(1px)",
      },

      "&:focus:not(:hover)": {
        background: bg,
        boxShadow: `${shadow}, 0 0 0 3px ${rgba(palette[color]?.main ?? dark.main, 0.3)}`,
      },

      "&:disabled": {
        background: bg,
        color: textColor,
        opacity: 0.48,
        boxShadow: "none",
        transform: "none",
        filter: "none",
      },
    };
  };

  // ------------------------------------------------------------------
  // Outlined – translucent gradient tint + 3D border lift
  // ------------------------------------------------------------------
  const outliedStyles = () => {
    const baseColor = palette[color] ? palette[color].main : white.main;
    const borderColor = palette[color] ? palette[color].main : rgba(white.main, 0.75);
    const bg     = `linear-gradient(145deg, ${rgba(baseColor, 0.07)} 0%, ${rgba(baseColor, 0.02)} 100%)`;
    const hoverBg = `linear-gradient(145deg, ${rgba(baseColor, 0.16)} 0%, ${rgba(baseColor, 0.07)} 100%)`;
    const shadow     = `inset 0 1px 0 rgba(255,255,255,0.18), 0 2px 6px rgba(0,0,0,0.1), 0 1px 3px ${rgba(baseColor, 0.2)}`;
    const hoverShad  = `inset 0 1px 0 rgba(255,255,255,0.22), 0 5px 14px rgba(0,0,0,0.14), 0 2px 7px ${rgba(baseColor, 0.32)}`;
    const activeShad = `inset 0 2px 5px rgba(0,0,0,0.18), 0 1px 2px rgba(0,0,0,0.12)`;

    return {
      background: bg,
      color: baseColor,
      borderColor,
      boxShadow: shadow,

      "&:hover": {
        background: hoverBg,
        borderColor: baseColor,
        boxShadow: hoverShad,
        transform: "translateY(-1px)",
      },

      "&:active, &:active:hover": {
        background: bg,
        boxShadow: activeShad,
        transform: "translateY(1px)",
      },

      "&:focus:not(:hover)": {
        background: bg,
        boxShadow: `${shadow}, 0 0 0 3px ${rgba(baseColor, 0.25)}`,
      },

      "&:disabled": {
        color: baseColor,
        borderColor,
        background: "transparent",
        boxShadow: "none",
        transform: "none",
      },
    };
  };

  // ------------------------------------------------------------------
  // Gradient – uses theme gradient palette + 3D depth
  // ------------------------------------------------------------------
  const gradientStyles = () => {
    const gPalette = palette.gradients[color];
    if (!gPalette) return containedStyles(); // fallback to contained

    // Strip positional percentages so we get raw colour values
    const stripPct = (str = "") => str.replace(/\s+[\d.]+%$/, "").trim();
    const from = stripPct(gPalette.main);
    const to   = stripPct(gPalette.state);
    const deg  = gPalette.deg ? `${parseFloat(gPalette.deg)}deg` : "145deg";

    const bg = `linear-gradient(${deg}, ${from} 0%, ${to} 100%)`;
    const shadow     = `inset 0 1px 0 rgba(255,255,255,0.28), 0 4px 14px rgba(0,0,0,0.25), 0 2px 6px rgba(0,0,0,0.15)`;
    const hoverShad  = `inset 0 1px 0 rgba(255,255,255,0.34), 0 8px 22px rgba(0,0,0,0.3), 0 4px 10px rgba(0,0,0,0.18)`;
    const activeShad = `inset 0 2px 6px rgba(0,0,0,0.35), 0 1px 3px rgba(0,0,0,0.2)`;

    return {
      background: bg,
      color: white.main,
      boxShadow: shadow,

      "&:hover": {
        background: bg,
        filter: "brightness(1.12)",
        boxShadow: hoverShad,
        transform: "translateY(-2px)",
      },

      "&:active, &:active:hover": {
        filter: "brightness(0.93)",
        boxShadow: activeShad,
        transform: "translateY(1px)",
      },

      "&:focus:not(:hover)": {
        boxShadow: `${shadow}, 0 0 0 3px rgba(255,255,255,0.22)`,
      },

      "&:disabled": {
        background: bg,
        color: white.main,
        opacity: 0.48,
        boxShadow: "none",
        transform: "none",
        filter: "none",
      },
    };
  };

  // ------------------------------------------------------------------
  // Text – ghost style with a subtle gradient background on interaction
  // ------------------------------------------------------------------
  const textStyles = () => {
    const baseColor = palette[color] ? palette[color].main : white.main;
    const focusColor = palette[color] ? palette[color].focus : baseColor;

    return {
      color: baseColor,
      boxShadow: "none",

      "&:hover": {
        color: focusColor,
        backgroundColor: rgba(baseColor, 0.09),
        boxShadow: `inset 0 1px 0 rgba(255,255,255,0.12), 0 2px 6px rgba(0,0,0,0.08)`,
        transform: "translateY(-1px)",
      },

      "&:active, &:active:hover": {
        color: focusColor,
        backgroundColor: rgba(baseColor, 0.14),
        boxShadow: "none",
        transform: "translateY(0)",
      },

      "&:focus:not(:hover)": {
        color: focusColor,
        boxShadow: `0 0 0 3px ${rgba(baseColor, 0.2)}`,
      },

      "&:disabled": {
        color: rgba(baseColor, 0.45),
        boxShadow: "none",
        transform: "none",
      },
    };
  };

  // styles for the button with circular={true}
  const circularStyles = () => ({
    borderRadius: borderRadius.section,
  });

  // styles for the button with iconOnly={true}
  const iconOnlyStyles = () => {
    let sizeValue = pxToRem(38);
    if (size === "small") sizeValue = pxToRem(25.4);
    else if (size === "large") sizeValue = pxToRem(52);

    let paddingValue = `${pxToRem(11)} ${pxToRem(11)} ${pxToRem(10)}`;
    if (size === "small") paddingValue = pxToRem(4.5);
    else if (size === "large") paddingValue = pxToRem(16);

    return {
      width: sizeValue,
      minWidth: sizeValue,
      height: sizeValue,
      minHeight: sizeValue,
      padding: paddingValue,
      "& .material-icons": { marginTop: 0 },
      "&:hover": { transform: "none" },
    };
  };

  return {
    ...(variant === "contained" && containedStyles()),
    ...(variant === "outlined"  && outliedStyles()),
    ...(variant === "gradient"  && gradientStyles()),
    ...(variant === "text"      && textStyles()),
    ...(circular   && circularStyles()),
    ...(iconOnly   && iconOnlyStyles()),
  };
});


